import React, { useEffect, useState } from 'react';
import { MediaImage } from '../ui/MediaImage';
import { fetchPassportEncounterMediaBlob } from '../../services/api/passport';

type Props = {
  encounterPublicId: string;
  mediaId: number;
  className?: string;
};

/**
 * Authenticated private encounter photo (direct /api/uploads paths are denied).
 */
const PassportEncounterMediaThumb: React.FC<Props> = ({
  encounterPublicId,
  mediaId,
  className = 'w-full aspect-square',
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const load = async () => {
      setFailed(false);
      setUrl(null);
      try {
        const blob = await fetchPassportEncounterMediaBlob(encounterPublicId, mediaId);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    void load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [encounterPublicId, mediaId]);

  if (failed) {
    return (
      <div
        className={`bg-black/40 flex items-center justify-center text-xs text-zinc-500 ${className}`}
        role="img"
        aria-label="Photo unavailable"
      >
        Unavailable
      </div>
    );
  }

  if (!url) {
    return (
      <div
        className={`bg-black/40 animate-pulse ${className}`}
        aria-hidden
      />
    );
  }

  return <MediaImage src={url} alt="Encounter photo" className={className} />;
};

export default PassportEncounterMediaThumb;
